import type React from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import type { client } from "@duress/shared";
import { Dialog, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useClients } from "@/features/clients/useClients";

export function ComparePickerDialog({
  open,
  onOpenChange,
  currentSlug,
  currentName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentSlug: string;
  currentName: string;
}): React.JSX.Element {
  const { data: clients } = useClients();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const options = useMemo<client.ClientListItem[]>(() => {
    if (!clients) return [];
    const q = query.trim().toLowerCase();
    return clients
      .filter((c) => c.slug !== currentSlug)
      .filter((c) => (q ? c.name.toLowerCase().includes(q) : true));
  }, [clients, currentSlug, query]);

  const onSelect = (slug: string): void => {
    onOpenChange(false);
    setQuery("");
    navigate(`/compare/${currentSlug}/${slug}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle>Compare clients</DialogTitle>
        <DialogDescription>
          Comparing with <strong>{currentName}</strong>. Pick a client to compare against.
        </DialogDescription>
      </DialogHeader>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          autoFocus
          placeholder="Search clients…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-8"
        />
      </div>

      <ul className="mt-4 max-h-80 overflow-y-auto divide-y divide-border rounded-md border border-border">
        {options.length === 0 ? (
          <li className="px-3 py-6 text-center text-sm text-muted-foreground">
            No matching clients.
          </li>
        ) : (
          options.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onSelect(c.slug)}
                className="group flex w-full items-center justify-between gap-3 px-3 py-3 text-left hover:bg-muted/50"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">{c.name}</p>
                  {c.industries.length > 0 ? (
                    <p className="truncate text-xs text-muted-foreground">
                      {c.industries.join(", ")}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">No industries</p>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-phoenix-orange" />
              </button>
            </li>
          ))
        )}
      </ul>
    </Dialog>
  );
}
